// The tile pass: what the tile front door (vortex-tiles) checks on every tile.
//
// @docs:
//   - docs/planning/TILE_FRONT_DOOR_PLAN.md §3.1
//
// vortex-api issues it once a customer is known to be entitled; the front door verifies
// it with one HMAC and no database, then counts the tile against `userId` and `clientId`.
// `sources` lists the registry ids the customer's plan opens, so a pass for one layer
// opens no other. Short-lived: the holder asks again for a new one. Web Crypto only.

export interface TilePass {
  userId: string;
  /** A device id, or a web app's name for its own pass. */
  clientId: string;
  /** Registry provider ids this pass opens. */
  sources: string[];
}

const enc = new TextEncoder();
const b64url = (bytes: Uint8Array) =>
  btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
const unb64url = (s: string) =>
  Uint8Array.from(atob(s.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (s.length % 4)) % 4)), (c) => c.charCodeAt(0));

async function hmac(secret: string): Promise<CryptoKey> {
  if (secret.length < 32) throw new Error('tile pass secret must be at least 32 characters');
  return crypto.subtle.importKey('raw', enc.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign', 'verify']);
}

/** `<base64url(json)>.<base64url(hmac)>`, valid for `ttlS` seconds from `nowMs`. */
export async function issueTilePass(secret: string, pass: TilePass, nowMs: number, ttlS: number): Promise<string> {
  const body = b64url(enc.encode(JSON.stringify({ v: 1, u: pass.userId, c: pass.clientId, s: pass.sources, e: Math.floor(nowMs / 1000) + ttlS })));
  const sig = new Uint8Array(await crypto.subtle.sign('HMAC', await hmac(secret), enc.encode(body)));
  return `${body}.${b64url(sig)}`;
}

/** The pass, or null when it is forged, altered, malformed or expired. */
export async function verifyTilePass(secret: string, token: string, nowMs: number): Promise<(TilePass & { expiresAt: number }) | null> {
  const [body, sig, extra] = token.split('.');
  if (!body || !sig || extra !== undefined) return null;
  try {
    if (!(await crypto.subtle.verify('HMAC', await hmac(secret), unb64url(sig), enc.encode(body)))) return null;
    const p = JSON.parse(new TextDecoder().decode(unb64url(body)));
    if (p.v !== 1 || typeof p.u !== 'string' || typeof p.c !== 'string' || !Array.isArray(p.s) || typeof p.e !== 'number') return null;
    if (p.e * 1000 <= nowMs) return null;
    return { userId: p.u, clientId: p.c, sources: p.s.map(String), expiresAt: p.e };
  } catch {
    return null;
  }
}
